import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';

import { ProductOrPrize, AppState } from '../../types/game';
import { Product } from '../../hooks/useProducts';

export interface GameState {
  items: ProductOrPrize[];
  loading: boolean;
  claimedPrizes: number;
  walletAmount: number;
  claimedPrizeIds: string[];
  appState: AppState;
  selectedProduct: Product | null;
  selectedProductVariants: Record<string, string>;
  selectedVariantId: string | null;
  distributedItems?: {
    type: string;
    id: string;
    claimed?: boolean;
  }[];
  hasShownSelectProductNotification: boolean;
}

const initialState: GameState = {
  items: [],
  loading: true,
  claimedPrizes: 0,
  walletAmount: 0,
  claimedPrizeIds: [],
  appState: 'collapsed',
  selectedProduct: null,
  selectedProductVariants: {},
  selectedVariantId: null,
  distributedItems: [],
  hasShownSelectProductNotification: false,
};

const gameSlice = createSlice({
  name: 'game',
  initialState,
  reducers: {
    setItems: (state, action: PayloadAction<ProductOrPrize[]>) => {
      state.items = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setClaimedPrizes: (state, action: PayloadAction<number>) => {
      state.claimedPrizes = action.payload;
    },
    incrementClaimedPrizes: (state) => {
      state.claimedPrizes += 1;
    },
    setWalletAmount: (state, action: PayloadAction<number>) => {
      state.walletAmount = action.payload;
    },
    addToWallet: (state, action: PayloadAction<number>) => {
      state.walletAmount += action.payload;
    },
    setClaimedPrizeIds: (state, action: PayloadAction<string[]>) => {
      state.claimedPrizeIds = action.payload;
    },
    addClaimedPrizeId: (state, action: PayloadAction<string>) => {
      if (!state.claimedPrizeIds.includes(action.payload)) {
        state.claimedPrizeIds.push(action.payload);
      }
    },
    setAppState: (state, action: PayloadAction<AppState>) => {
      state.appState = action.payload;
    },
    setSelectedProduct: (state, action: PayloadAction<Product | null>) => {
      state.selectedProduct = action.payload;
    },
    setSelectedProductVariants: (state, action: PayloadAction<Record<string, string>>) => {
      state.selectedProductVariants = action.payload;
    },
    setSelectedVariantId: (state, action: PayloadAction<string | null>) => {
      state.selectedVariantId = action.payload;
    },
    setDistributedItems: (state, action: PayloadAction<{ type: string; id: string; claimed?: boolean }[]>) => {
      state.distributedItems = action.payload;
    },
    setHasShownSelectProductNotification: (state, action: PayloadAction<boolean>) => {
      state.hasShownSelectProductNotification = action.payload;
    },
    initializeGameState: (state, action: PayloadAction<Partial<GameState>>) => {
      Object.assign(state, action.payload);
    },
    clearPersistedGameState: () => {
      // Complete reset including persisted state
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // Handle rehydration from redux-persist
    builder.addCase(REHYDRATE, (state, action: any) => {
      if (action.payload?.game) {
        // Merge rehydrated state but ensure items array is reset (will be regenerated)
        const rehydratedGame = action.payload.game;
        return {
          ...state,
          ...rehydratedGame,
          items: [], // Always start with empty items (will be regenerated)
          loading: true, // Always start loading (will be updated by thunks)
        };
      }
    });
  },
});

export const {
  setItems,
  setLoading,
  setClaimedPrizes,
  incrementClaimedPrizes,
  setWalletAmount,
  addToWallet,
  setClaimedPrizeIds,
  addClaimedPrizeId,
  setAppState,
  setSelectedProduct,
  setSelectedProductVariants,
  setSelectedVariantId,
  setDistributedItems,
  setHasShownSelectProductNotification,
  initializeGameState,
  clearPersistedGameState,
} = gameSlice.actions;

export default gameSlice.reducer;
